import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import ArticleCard from '../../components/academy/ArticleCard';
import Breadcrumbs from '../../components/academy/Breadcrumbs';

const API = process.env.REACT_APP_BACKEND_URL;

const CategoryPage = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/academy/categories/${slug}?page=${page}&limit=20`);
        if (!res.ok) throw new Error('Not found');
        const data = await res.json();
        setCategory(data);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug, page]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#115E59] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Category Not Found</h1>
          <Link to="/academy" className="text-[#115E59] font-semibold hover:underline">Return to Academy</Link>
        </div>
      </div>
    );
  }

  const categoryName = category.name || slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const articles = category.articles || [];
  const totalPages = category.pages || 1;

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      {/* Header */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8 pt-8 pb-10">
          <Breadcrumbs category={slug} categoryName={categoryName} />

          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-6 mb-3" style={{ fontFamily: 'var(--font-heading)' }} data-testid="category-title">
            {categoryName}
          </h1>
          {category.description && (
            <p className="text-base text-slate-500 max-w-2xl">{category.description}</p>
          )}
          <p className="text-sm text-slate-400 mt-3">{category.total || 0} articles</p>
        </div>
      </section>

      {/* Articles */}
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        {articles.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-500">No articles in this category yet.</p>
            <Link to="/academy" className="text-[#115E59] font-semibold hover:underline mt-2 inline-block">Browse all topics</Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map(article => (
                <ArticleCard key={article.slug} article={article} variant="standard" />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-12">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    onClick={() => { setPage(p); window.scrollTo(0, 0); }}
                    className={`w-10 h-10 rounded-full text-sm font-semibold transition-colors ${
                      p === page ? 'bg-[#115E59] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* CTA */}
      <section className="py-12 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Ready to Take Action?
          </h2>
          <p className="text-sm text-slate-500 mb-6">Apply what you have learned with Quidity's financing solutions.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/apply" className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold text-sm px-6 py-3 rounded-full hover:bg-slate-800 transition-all">
              Apply Now <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/academy" className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 font-semibold text-sm px-6 py-3 rounded-full hover:bg-slate-50 transition-all">
              Back to Academy <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CategoryPage;
